import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { renderMarkdown, Button } from "@impero-com/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { safeZodPath } from "../../utils/form/safeZodPath";
import { t } from "../../utils/i18n";
import { assertNever } from "../../utils/obj";
import { DeleteWithConfirmationDialog } from "../deleteWithConfirmationDialog/DeleteWithConfirmationDialog";
import { RHFDatePickerInput } from "../form/rhfFields/rhfDatePickerInput";
import { FormSection } from "../formSection/formSection";

import * as theme from "./theme.css";

const schema = z
    .object({
        startDate: z.date(),
    })
    .check((ctx) => {
        const val = ctx.value;
        if (val.startDate.getMonth() === 1 && val.startDate.getDate() >= 29) {
            ctx.issues.push({
                code: "custom",
                path: safeZodPath<FinancialYearValues>("startDate"),
                message: "Invalid",
                input: ctx.value,
            });
        }
    });

export type FinancialYearValues = z.infer<typeof schema>;

interface FinancialYearFormProps {
    mode:
        | { type: "CREATE" }
        | { type: "UPDATE"; initialValues: FinancialYearValues };
    onSave: (values: FinancialYearValues) => Promise<unknown>;
    onDelete: () => Promise<unknown>;
    isSaving: boolean;
    isDeleting: boolean;
    backLink?: React.ReactNode;
}

export function FinancialYearForm({
    mode,
    onSave,
    onDelete,
    isSaving,
    isDeleting,
    backLink,
}: FinancialYearFormProps) {
    let saveLabel: string;
    let defaultValues: FinancialYearValues;
    switch (mode.type) {
        case "CREATE": {
            saveLabel = t("CreateControlProgram_Create");
            const startDate = new Date();
            startDate.setMonth(0);
            startDate.setDate(1);
            defaultValues = {
                startDate,
            };
            break;
        }
        case "UPDATE": {
            saveLabel = t("EditControlProgram_Update");
            defaultValues = mode.initialValues;
            break;
        }
        default: {
            assertNever(mode);
        }
    }

    const { control, formState, handleSubmit, reset } =
        useForm<FinancialYearValues>({
            mode: "onBlur",
            resolver: zodResolver(schema),
            defaultValues,
        });

    const [isConfirmingDeletion, setIsConfirmingDeletion] =
        React.useState(false);

    const handleSubmitClick = handleSubmit(
        async (data: FinancialYearValues) => {
            await onSave(data);
        },
        (error) => {
            console.error(error);
        }
    );

    const handleDelete = async () => {
        await onDelete();
    };

    const handleCancel = () => {
        reset();
    };

    return (
        <FormSection id="financial-year">
            {backLink}
            <RHFDatePickerInput
                label={t("Financial_Year_StartDate")}
                control={control}
                name={"startDate"}
                schema={z.date()}
                format="MM-DD"
                disabledDate={(date) => {
                    // 29-02.
                    //
                    // It's not that it wouldn't work in the most sensible way
                    // the scheduler can understand (c.f. the v2 scheduler).
                    // This code could also be removed as a FY with 31-02 is
                    // also understood by the scheduler in the most sensible way:
                    // it would mean the FY start on "the last day of February"
                    // and ends on "the first day of March".
                    // We are also considering the removal of the day part of the
                    // FY. So this is more a POC than an actual check. Nobody will
                    // notice.
                    return date.month() === 1 && date.date() >= 29;
                }}
            />
            <div>
                <Button
                    size="large"
                    variant="primary"
                    className={theme.button}
                    disabled={
                        (mode.type !== "CREATE" && !formState.isDirty) ||
                        isConfirmingDeletion ||
                        isDeleting
                    }
                    loading={isSaving}
                    onClick={() => void handleSubmitClick()}
                >
                    {saveLabel}
                </Button>
                {mode.type === "UPDATE" && (
                    <Button
                        size="large"
                        variant="destructive"
                        className={theme.button}
                        disabled={
                            isSaving || isConfirmingDeletion || isDeleting
                        }
                        loading={isDeleting}
                        onClick={() => setIsConfirmingDeletion(true)}
                    >
                        {t("RiskDirectory_NodeUpdate_Delete")}
                    </Button>
                )}
                <Button
                    size="large"
                    variant="default"
                    className={theme.button}
                    disabled={
                        !formState.isDirty ||
                        isSaving ||
                        isConfirmingDeletion ||
                        isDeleting
                    }
                    onClick={handleCancel}
                >
                    {t("Cancel")}
                </Button>
                <DeleteWithConfirmationDialog
                    title={t("Financial_Year_Delete_Title")}
                    open={isConfirmingDeletion}
                    onConfirm={() => void handleDelete()}
                    onCancel={() => {
                        setIsConfirmingDeletion(false);
                    }}
                >
                    {renderMarkdown(t("Financial_Year_Delete_Body"))}
                </DeleteWithConfirmationDialog>
            </div>
        </FormSection>
    );
}
